import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { FaUserSlash } from 'react-icons/fa6';
import {
  createSuspension,
  getSuspensionHistory,
  liftSuspension,
  getTicketSuspensionScore,
  getTicketPointHistory,
  invalidateTicketPointMovement,
} from '../auth/suspensionApi';
import './SuspendUserModal.css';

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.('frontElakayHardcodedText', __elakayInfoSource.languageAPI)?.['suspendusermodal']?.[key] || fallback;

// Mirrors elakay_services/roleActivation.js's ROLE_CONFIG plus the implicit
// "User" base level -- kept in the exact hierarchy order the admin picks
// from, User..Family (spec §2).
const ROLE_OPTIONS = [
  { value: 'User', label: () => __elakayText('t001', 'User') },
  { value: 'Royalty', label: () => __elakayText('t002', 'User Royale') },
  { value: 'Seller', label: () => __elakayText('t003', 'Seller') },
  { value: 'Provider', label: () => __elakayText('t004', 'Provider') },
  { value: 'Associate', label: () => __elakayText('t005', 'Associate') },
  { value: 'Investment', label: () => __elakayText('t006', 'Investor') },
  { value: 'Administrator', label: () => __elakayText('t007', 'Administrator') },
  { value: 'Family', label: () => __elakayText('t008', "Developer") },
];

const DURATION_PRESETS = [
  { days: 7, label: () => __elakayText('t009', '7 days') },
  { days: 14, label: () => __elakayText('t010', '14 days') },
  { days: 21, label: () => __elakayText('t011', '21 days') },
  { days: 30, label: () => __elakayText('t012', '30 days') },
  { days: 60, label: () => __elakayText('t013', '60 days') },
  { days: 90, label: () => __elakayText('t014', '90 days') },
  { days: 180, label: () => __elakayText('t015', '6 months') },
  { days: 365, label: () => __elakayText('t016', '12 months') },
];

const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const localTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
};

const formatDate = (value) => {
  if (!value) return __elakayText('t017', 'Unlimited');
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

const STATUS_LABELS = {
  scheduled: () => __elakayText('t018', 'Scheduled'),
  active: () => __elakayText('t019', 'Active'),
  expired: () => __elakayText('t020', 'Expired'),
  lifted: () => __elakayText('t021', 'Lifted'),
  superseded: () => __elakayText('t022', 'Superseded'),
};

const DEFAULT_FORM = {
  roleName: 'User',
  reason: '',
  description: '',
  startMode: 'immediate',
  startAt: '',
  endMode: 'unlimited',
  presetDays: 7,
  endAt: '',
  notifyEmail: true,
};

const SuspendUserModal = ({ show, onHide, token, userId, userFullName, infoSource, onSuspended }) => {
  __setElakayInfoSource(infoSource);

  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const [liftTargetId, setLiftTargetId] = useState(null);
  const [liftReason, setLiftReason] = useState('');
  const [liftSubmitting, setLiftSubmitting] = useState(false);

  // "Ticket Suspension Score" (spec §20/§22) -- current points, max,
  // remaining, and the full point-movement ledger for this user.
  const [score, setScore] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [pointMovements, setPointMovements] = useState([]);
  const [showPointHistory, setShowPointHistory] = useState(false);
  const [invalidatingId, setInvalidatingId] = useState(null);

  const loadHistory = () => {
    setHistoryLoading(true);
    setHistoryError(null);
    getSuspensionHistory(token, userId).then((data) => {
      if (data?.success) {
        setHistory(data.suspensions || []);
      } else {
        setHistoryError(data?.error || __elakayText('t023', 'Unable to load suspension history.'));
      }
      setHistoryLoading(false);
    });
  };

  const loadScore = () => {
    setScoreLoading(true);
    Promise.all([
      getTicketSuspensionScore(token, userId),
      getTicketPointHistory(token, userId),
    ]).then(([scoreData, historyData]) => {
      if (scoreData?.success) setScore(scoreData.score);
      if (historyData?.success) setPointMovements(historyData.movements || []);
      setScoreLoading(false);
    });
  };

  const handleInvalidateMovement = async (movementId) => {
    setInvalidatingId(movementId);
    const data = await invalidateTicketPointMovement(token, movementId, '');
    setInvalidatingId(null);
    if (data?.success) {
      loadScore();
    }
  };

  useEffect(() => {
    if (show && userId) {
      setForm(DEFAULT_FORM);
      setFormError(null);
      setFormSuccess(null);
      loadHistory();
      loadScore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, userId]);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const effectiveStart = form.startMode === 'scheduled' && form.startAt ? new Date(form.startAt) : new Date();
  const specificEnd = form.endAt ? new Date(form.endAt) : null;
  const isFormValid = Boolean(form.reason.trim())
    && (form.startMode !== 'scheduled' || (form.startAt && effectiveStart > new Date()))
    && (form.endMode !== 'date' || (specificEnd && specificEnd > effectiveStart))
    && (form.endMode !== 'preset' || Number(form.presetDays) > 0);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    if (!form.reason.trim()) {
      setFormError(__elakayText('t024', 'A reason is required.'));
      return;
    }
    if (form.startMode === 'scheduled' && !form.startAt) {
      setFormError(__elakayText('t025', 'Please choose a start date and time.'));
      return;
    }
    if (form.endMode === 'date' && !form.endAt) {
      setFormError(__elakayText('t026', 'Please choose an end date and time.'));
      return;
    }
    if (form.endMode === 'date' && new Date(form.endAt) <= effectiveStart) {
      setFormError(__elakayText('t094', 'End date must be after the suspension start.'));
      return;
    }

    setSubmitting(true);
    const payload = {
      roleName: form.roleName,
      reason: form.reason.trim(),
      description: form.description.trim(),
      notifyEmail: form.notifyEmail,
      startAt: form.startMode === 'scheduled' ? new Date(form.startAt).toISOString() : undefined,
      endAt: form.endMode === 'date' ? new Date(form.endAt).toISOString() : null,
      duration: form.endMode === 'preset' ? form.presetDays : undefined,
    };

    const data = await createSuspension(token, userId, payload);
    setSubmitting(false);
    if (data?.success) {
      setFormSuccess(__elakayText('t027', 'Suspension applied.'));
      setForm(DEFAULT_FORM);
      loadHistory();
      loadScore();
      onSuspended && onSuspended();
    } else {
      setFormError(data?.error || __elakayText('t028', 'Unable to apply suspension.'));
    }
  };

  const confirmLift = async (suspensionId) => {
    setLiftSubmitting(true);
    const data = await liftSuspension(token, suspensionId, liftReason.trim());
    setLiftSubmitting(false);
    if (data?.success) {
      setLiftTargetId(null);
      setLiftReason('');
      loadHistory();
      loadScore();
      onSuspended && onSuspended();
    } else {
      setHistoryError(data?.error || __elakayText('t029', 'Unable to lift this suspension.'));
    }
  };

  const currentlyGoverning = history.find((s) => s.status === 'active' || s.status === 'scheduled');

  return (
    <Modal show={show} onHide={onHide} dialogClassName="suspend-user-modal" size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUserSlash style={{ marginRight: 8 }} />
          {__elakayText('t030', 'Suspend')} {userFullName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {currentlyGoverning && (
          <div className="suspend-user-current-banner">
            {__elakayText('t031', 'Currently under suspension:')}{' '}
            <strong>{currentlyGoverning.level?.roleName}</strong> —{' '}
            {STATUS_LABELS[currentlyGoverning.status]?.() || currentlyGoverning.status}
          </div>
        )}

        <div className="suspend-user-score">
          <h6 className="suspend-user-score-title">{__elakayText('t085', 'Ticket Suspension Score')}</h6>
          {scoreLoading && <div className="suspend-user-history-empty">{__elakayText('t046', 'Loading…')}</div>}
          {!scoreLoading && score && (
            <>
              <div className="suspend-user-score-grid">
                <div><span>{__elakayText('t086', 'Current points')}</span><strong>{score.totalPoints}</strong></div>
                <div><span>{__elakayText('t064', 'Maximum total points')}</span><strong>{score.maximumTotalPoints ?? '—'}</strong></div>
                <div><span>{__elakayText('t087', 'Remaining before suspension')}</span><strong>{score.remaining ?? '—'}</strong></div>
                <div><span>{__elakayText('t071', 'Buyer')}</span><strong>{score.buyerCount}</strong></div>
                <div><span>{__elakayText('t048', 'Automatic')}</span><strong>{score.automaticCount}</strong></div>
                <div><span>{__elakayText('t088', 'Last ticket')}</span><strong>{formatDate(score.lastTicketDate) || '—'}</strong></div>
              </div>
              {score.warningsCrossed?.length > 0 && (
                <div className="suspend-user-score-warning">
                  {__elakayText('t089', 'Warning thresholds crossed:')} {score.warningsCrossed.join('%, ')}%
                </div>
              )}
              <button type="button" className="suspend-user-lift-btn" onClick={() => setShowPointHistory((v) => !v)}>
                {__elakayText('t090', 'Points history')} ({pointMovements.length})
              </button>
              {showPointHistory && (
                <ul className="suspend-user-point-history">
                  {pointMovements.map((m) => (
                    <li key={m._id} className={m.status === 'invalidated' ? 'suspend-user-point-history-invalidated' : ''}>
                      <span>{formatDate(m.createdAt)}</span>
                      <span>{m.ticketSource === 'automatic' ? (m.automaticRuleKey || __elakayText('t048', 'Automatic')) : __elakayText('t071', 'Buyer')}</span>
                      <span>+{m.points} {__elakayText('t093', 'Points')}</span>
                      <span>{m.pointsBefore} → {m.pointsAfter}</span>
                      {m.status === 'invalidated' ? (
                        <span className="suspend-user-point-history-tag">{__elakayText('t091', 'Invalidated')}</span>
                      ) : (
                        <button
                          type="button"
                          disabled={invalidatingId === m._id}
                          onClick={() => handleInvalidateMovement(m._id)}
                        >
                          {__elakayText('t092', 'Invalidate')}
                        </button>
                      )}
                    </li>
                  ))}
                  {pointMovements.length === 0 && <li>{__elakayText('t047', 'No suspension on record for this user.')}</li>}
                </ul>
              )}
            </>
          )}
        </div>

        <form onSubmit={handleSubmit} className="suspend-user-form">
          <div className="suspend-user-field">
            <label htmlFor="suspend-user-role">{__elakayText('t032', 'Suspension level')}</label>
            <select id="suspend-user-role" value={form.roleName} onChange={(e) => updateField('roleName', e.target.value)}>
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label()}</option>
              ))}
            </select>
            <small>{__elakayText('t033', 'This role and every role above it become inaccessible; lower roles stay active.')}</small>
          </div>

          <div className="suspend-user-field">
            <label htmlFor="suspend-user-reason">{__elakayText('t034', 'Reason (required)')}</label>
            <input
              id="suspend-user-reason"
              type="text"
              maxLength={500}
              value={form.reason}
              onChange={(e) => updateField('reason', e.target.value)}
              required
            />
          </div>

          <div className="suspend-user-field">
            <label htmlFor="suspend-user-description">{__elakayText('t035', 'Detailed description')}</label>
            <textarea
              id="suspend-user-description"
              rows={3}
              maxLength={3000}
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
            />
          </div>

          <div className="suspend-user-field-row">
            <div className="suspend-user-field">
              <label>{__elakayText('t036', 'Start')}</label>
              <div className="suspend-user-radio-group" role="radiogroup" aria-label={__elakayText('t036', 'Start')}>
                <label className={form.startMode === 'immediate' ? 'is-selected' : ''}>
                  <input
                    type="radio"
                    name="startMode"
                    checked={form.startMode === 'immediate'}
                    onChange={() => updateField('startMode', 'immediate')}
                  />
                  {__elakayText('t037', 'Immediately')}
                </label>
                <label className={form.startMode === 'scheduled' ? 'is-selected' : ''}>
                  <input
                    type="radio"
                    name="startMode"
                    checked={form.startMode === 'scheduled'}
                    onChange={() => updateField('startMode', 'scheduled')}
                  />
                  {__elakayText('t038', 'Scheduled')}
                </label>
              </div>
              {form.startMode === 'scheduled' && (
                <input
                  type="datetime-local"
                  aria-label={__elakayText('t096', 'Start date and time')}
                  min={toDateTimeLocalValue(new Date())}
                  value={form.startAt}
                  onChange={(e) => updateField('startAt', e.target.value)}
                />
              )}
            </div>

            <div className="suspend-user-field">
              <label>{__elakayText('t039', 'End')}</label>
              <div className="suspend-user-radio-group" role="radiogroup" aria-label={__elakayText('t039', 'End')}>
                <label className={form.endMode === 'preset' ? 'is-selected' : ''}>
                  <input
                    type="radio"
                    name="endMode"
                    checked={form.endMode === 'preset'}
                    onChange={() => updateField('endMode', 'preset')}
                  />
                  {__elakayText('t040', 'Duration')}
                </label>
                <label className={form.endMode === 'date' ? 'is-selected' : ''}>
                  <input
                    type="radio"
                    name="endMode"
                    checked={form.endMode === 'date'}
                    onChange={() => updateField('endMode', 'date')}
                  />
                  {__elakayText('t041', 'Specific date')}
                </label>
                <label className={form.endMode === 'unlimited' ? 'is-selected' : ''}>
                  <input
                    type="radio"
                    name="endMode"
                    checked={form.endMode === 'unlimited'}
                    onChange={() => updateField('endMode', 'unlimited')}
                  />
                  {__elakayText('t042', 'Unlimited')}
                </label>
              </div>
              {form.endMode === 'preset' && (
                <select
                  aria-label={__elakayText('t097', 'Suspension duration')}
                  value={form.presetDays}
                  onChange={(e) => updateField('presetDays', Number(e.target.value))}
                >
                  {DURATION_PRESETS.map((preset) => (
                    <option key={preset.days} value={preset.days}>{preset.label()}</option>
                  ))}
                </select>
              )}
              {form.endMode === 'date' && (
                <input
                  type="datetime-local"
                  aria-label={__elakayText('t098', 'End date and time')}
                  min={toDateTimeLocalValue(effectiveStart)}
                  value={form.endAt}
                  onChange={(e) => updateField('endAt', e.target.value)}
                />
              )}
              {form.endMode === 'unlimited' && <small>{__elakayText('t095', 'No automatic reinstatement date. Access must be restored manually.')}</small>}
            </div>
          </div>

          <div className="suspend-user-field">
            <label className="suspend-user-checkbox-label">
              <input
                type="checkbox"
                className="suspend-user-checkbox"
                checked={form.notifyEmail}
                onChange={(e) => updateField('notifyEmail', e.target.checked)}
              />
              {__elakayText('t043', 'Notify the user by email')}
            </label>
          </div>

          {formError && <div className="suspend-user-alert suspend-user-alert-error">{formError}</div>}
          {formSuccess && <div className="suspend-user-alert suspend-user-alert-success">{formSuccess}</div>}

          <button type="submit" className="suspend-user-submit" disabled={submitting || !isFormValid}>
            {submitting ? __elakayText('t044', 'Applying…') : __elakayText('t030', 'Suspend')}
          </button>
        </form>

        <hr />

        <h6 className="suspend-user-history-title">{__elakayText('t045', 'Suspension history')}</h6>
        {historyLoading && <div className="suspend-user-history-empty">{__elakayText('t046', 'Loading…')}</div>}
        {!historyLoading && historyError && <div className="suspend-user-alert suspend-user-alert-error">{historyError}</div>}
        {!historyLoading && !historyError && history.length === 0 && (
          <div className="suspend-user-history-empty">{__elakayText('t047', 'No suspension on record for this user.')}</div>
        )}
        {!historyLoading && !historyError && history.length > 0 && (
          <ul className="suspend-user-history-list">
            {history.map((s) => (
              <li key={s._id} className={`suspend-user-history-item suspend-user-history-item--${s.status}`}>
                <div className="suspend-user-history-head">
                  <strong>{s.level?.roleName}</strong>
                  <span className={`suspend-user-status-pill suspend-user-status-pill--${s.status}`}>
                    {STATUS_LABELS[s.status]?.() || s.status}
                  </span>
                  <span className="suspend-user-history-origin">
                    {s.origin === 'automatic' ? __elakayText('t048', 'Automatic') : __elakayText('t049', 'Manual')}
                  </span>
                </div>
                <div className="suspend-user-history-reason">{s.reason}</div>
                {s.description && <div className="suspend-user-history-description">{s.description}</div>}
                <div className="suspend-user-history-dates">
                  {formatDate(s.startAt)} → {formatDate(s.endAt)}
                </div>

                {s.origin === 'automatic' && s.automaticRuleSnapshot && (
                  <div className="suspend-user-auto-rule">
                    <div>
                      {__elakayText('t058', 'Points at trigger:')} {s.automaticRuleSnapshot.totalPoints}
                      {' / '}
                      {s.automaticRuleSnapshot.maximumTotalPoints}
                    </div>
                  </div>
                )}
                {s.lift?.liftedAt && (
                  <div className="suspend-user-history-lift">
                    {__elakayText('t050', 'Lifted on')} {formatDate(s.lift.liftedAt)}
                    {s.lift.liftReason ? ` — ${s.lift.liftReason}` : ''}
                  </div>
                )}
                {s.responses?.length > 0 && (
                  <div className="suspend-user-history-responses">
                    <strong>{__elakayText('t051', 'User responses:')}</strong>
                    {s.responses.map((r, idx) => (
                      <div key={idx} className="suspend-user-history-response">
                        {formatDate(r.createdAt)}: {r.text}
                      </div>
                    ))}
                  </div>
                )}

                {(s.status === 'active' || s.status === 'scheduled') && (
                  liftTargetId === s._id ? (
                    <div className="suspend-user-lift-form">
                      <input
                        type="text"
                        placeholder={__elakayText('t052', 'Reason for lifting (optional)')}
                        value={liftReason}
                        onChange={(e) => setLiftReason(e.target.value)}
                      />
                      <button type="button" disabled={liftSubmitting} onClick={() => confirmLift(s._id)}>
                        {__elakayText('t053', 'Confirm')}
                      </button>
                      <button type="button" onClick={() => { setLiftTargetId(null); setLiftReason(''); }}>
                        {__elakayText('t054', 'Cancel')}
                      </button>
                    </div>
                  ) : (
                    <button type="button" className="suspend-user-lift-btn" onClick={() => setLiftTargetId(s._id)}>
                      {__elakayText('t055', 'Lift now')}
                    </button>
                  )
                )}
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SuspendUserModal;
